<template>
  <div :class="['m-overview-card m-overview-card--mobile', cardClassName]">
    <div class="m-overview-card__container">
      <div
        v-if="data && data.copyright != undefined"
        class="m-overview-card__copyright"
      >
        {{ data.copyright }}
      </div>
      <div class="m-overview-card__content-container">
        <GuiLink :to="search" class="m-overview-card--mobile__link">
          <div class="m-overview-card__info-container">
            <div class="m-overview-card__category-label">
              {{ $t(`overview.${categoryId}`) }}
            </div>
            <div
              class="m-overview-card__left-curtain"
              :style="{ width: `${dataWidth}%` }"
            >
              <div
                class="m-overview-card__percentage"
                :style="{
                  transform: `translate(calc(${
                    percentageLabelOnLeft ? 0 : 100
                  }% ${
                    percentageLabelOnLeft ? `` : ` + ${percentageMargin}px`
                  }), 0)`
                }"
              >
                {{ formattedDatum }}
              </div>
            </div>
          </div>
        </GuiLink>
        <div class="m-overview-card__image-container">
          <router-link
            :to="search"
            :class="['a-link--wraps-component']"
            tabindex="-1"
            iconsize="text"
          >
            <ImageResponsive
              :id="id"
              class="m-overview-card__responsive-image"
              size="post_medium_variable_height"
              :region="pctRegion"
              :alt="data.title"
              :title="data.title"
            />
          </router-link>
        </div>
        <div class="m-overview-card__arrow-container">
          <router-link
            :to="search"
            class="a-link--wraps-component"
            tabindex="-1"
            iconsize="text"
            :aria-label="$t(`overview.${categoryId}`)"
          >
            <Icon name="outbound" size="full" />
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ImageResponsive, GuiLink, Icon } from "@thegetty-private/getty-ui";
import { scaleLinear } from "d3-scale";
import { formatPercentage, constructSearch } from "@/helpers/overviewHelper";
export default {
  name: "OverviewCardMobile",
  components: { ImageResponsive, GuiLink, Icon },
  props: {
    /**
     * Card id
     *
     * @type {String}
     */
    id: {
      type: String,
      required: true
    },
    /**
     * Percentage value [0, 100] that indicates how many objects of that category the Getty Museum owns
     *
     * @type {Number}
     */
    datum: {
      type: Number,
      required: true
    },
    /**
     * Id of the category the card belongs
     *
     * @type {String}
     */
    categoryId: {
      type: String,
      required: true
    },
    /**
     * CSS class that indicates the theme color to use
     *
     * @type {String}
     */
    cardClassName: {
      type: String,
      required: true
    },
    /**
     * String indicating the image ratio
     *
     * @type {String}
     */
    ratio: {
      type: String,
      required: true
    },
    /**
     * An Object containing the crop regions for each aspect ratio at which an image can be rendered at (full, 2:3, 16:9, no-ratio)
     * region - "full" or any of the ratios supported by the size you've chosen from the presets {@link src/utils/imagePresets.json}
     *
     * @type {Object}
     *
     *  region - "full" or any of the ratios supported by the size you've chosen from the presets {@link src/utils/imagePresets.json}
     *  x - the x offset of the crop for the image at full resolution
     *  y - the y offset of the crop for the image at full resolution
     *  w - the width of the crop at the image's full resolution
     *  h - the height of the crop at the image's full resolution
     *
     * @example { "1:1": { x:0, y:0, w: 800, h: 800 }, "16:9": {...}}
     */
    region: {
      type: Object,
      required: false,
      default: undefined
    },
    /**
     * The router-link "to" property
     */
    to: {
      type: Object,
      default: undefined
    },
    /**
     * The object's metadata
     */
    data: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      percentageMargin: 4
    };
  },
  computed: {
    /**
     * Return colored rect width (in pixels) that rapresents the datum value
     * @returns {Number}
     */
    dataWidth() {
      // percentage scale: from percentage datum value to container percentage width
      // minimum container percentage value is > 0 to ensure that the darkest bar is always visible even if the datum is very small
      const widthScale = scaleLinear([0, 100], [5, 100]);
      return widthScale(this.datum);
    },
    /**
     * Return true if datum value is greater or equals to 95. Used to place the percentage label on the left or on the right of the darker rect
     * @returns {Boolean}
     */
    percentageLabelOnLeft() {
      return this.datum >= 95;
    },
    /**
     * Return the percentage value to show
     *
     * @returns {String}
     */
    formattedDatum() {
      return `${formatPercentage(this.datum)}%`;
    },
    /**
     * Return the region object with percentage values
     *
     * @returns {Object}
     */
    pctRegion() {
      return {
        [this.ratio]: {
          ...this.region[this.ratio],
          x: `pct:${this.region[this.ratio].x}`
        }
      };
    },
    /**
     * Returns the search route params Object or undefined for a GuiLink
     *
     * @return {Object} | undefined
     */
    search() {
      if (this.data.search != undefined) {
        return constructSearch(this.data.search);
      }
      return undefined;
    }
  }
};
</script>

<style lang="scss">
@import "./mobile.scss";
</style>
